import type { AuthN } from '@seek/online-identity';
import axios from 'axios';

import type { Router } from 'src/constants/urls';
import {
  getContentDispositionFilename,
  saveBlob,
} from 'src/shared/fileDownload';

import type { AnalyticsDocumentType } from '../analytics';

export const attachmentTypes = {
  RESUME: 1,
  COVER_LETTER: 2,
  SELECTION_CRITERIA: 4,
} as const;

export type AttachmentType = keyof typeof attachmentTypes;

const attachmentTypeToAnalyticsDocumentType = {
  RESUME: 'resume',
  COVER_LETTER: 'cover letter',
  SELECTION_CRITERIA: 'selection criteria',
} satisfies Record<AttachmentType, AnalyticsDocumentType>;

type Result =
  | {
      success: true;
      attachmentId: string;
      analyticsAttachmentType: AnalyticsDocumentType;
    }
  | {
      success: false;
    };

export const downloadAttachment = async ({
  baseApiPath,
  prospectId,
  authN,
  attachmentId,
  attachmentType,
  router,
}: {
  authN: AuthN;
  baseApiPath: string;
  prospectId: number;
  attachmentId: string;
  attachmentType: AttachmentType;
  router: Router;
}): Promise<Result> => {
  try {
    const token = await authN.getToken();

    if (typeof token !== 'string') {
      authN.login();
      return {
        success: false,
      };
    }

    await downloadFileFromAttachmentService({
      url: router.getAttachment({
        attachmentId,
        prospectId,
        prefix: baseApiPath,
      }),
      token,
    });

    return {
      success: true,
      attachmentId,
      analyticsAttachmentType:
        attachmentTypeToAnalyticsDocumentType[attachmentType],
    };
  } catch (_e) {
    return { success: false };
  }
};

const downloadFileFromAttachmentService = async ({
  url,
  token,
}: {
  url: string;
  token: string;
}) => {
  const response = await axios.get(url, {
    responseType: 'blob',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const disposition = response.request.getResponseHeader('content-disposition');
  const dispositionFilename = getContentDispositionFilename(disposition);
  saveBlob({
    blob: response.data,
    fileName: dispositionFilename,
  });
};
